import { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Empty, Form, Input, message, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, MailOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import TipTapEditor from '../../../components/TipTapEditor';
import { getAdminClients, getModuleFields, getModules, getRowActionConfigs } from '../../../services/adminApi';
import { createEmailTemplate, createSignature, deleteEmailTemplate, deleteSignature, getEmailTemplate, getEmailTemplates, getSignature, getSignatures, updateEmailTemplate, updateSignature, updateSignatureStatus } from '../../../services/emailTemplateApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;
const EMPTY = { name: '', module: '', action: '', clientId: '', description: '', toMail: [], ccMail: [], bccMail: [], subject: '', body: '', isActive: true };
const idOf = (record) => record?._id ?? record?.id;

function extractPlaceholderKeys(...values) {
  const keys = new Set();
  values.forEach((value) => {
    const regex = /{{\.([\w.]+)}}/g;
    let match;
    while ((match = regex.exec(String(value ?? ''))) !== null) keys.add(match[1]);
  });
  return keys;
}

function TemplateEditor({ open, templateId, modules, clients, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionOptions, setActionOptions] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);

  async function loadModuleMetadata(module) {
    if (!module) { setActionOptions([]); setFieldOptions([]); return; }
    const [configs, fields] = await Promise.all([
      getRowActionConfigs(module).catch(() => []),
      getModuleFields(module).catch(() => []),
    ]);
    const actions = new Map();
    configs.forEach((config) => (config.rowActions ?? []).forEach((action) => {
      if (action.key) actions.set(action.key, action.label || action.key);
    }));
    setActionOptions([...actions].map(([value, label]) => ({ value, label })));
    setFieldOptions(fields.map((field) => ({
      key: field.value ?? field.field ?? field.fieldKey ?? field.key ?? field.name,
      value: field.path ?? field.value ?? field.field ?? field.fieldKey ?? field.key ?? field.name,
      label: field.label ?? field.fieldName ?? field.name ?? field.value,
    })).filter((field) => field.key));
  }

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const request = templateId ? getEmailTemplate(templateId) : Promise.resolve(EMPTY);
    request.then((item) => {
      if (cancelled) return;
      const values = { ...EMPTY, ...item, clientId: item?.clientIdStr || (typeof item?.clientId === 'string' ? item.clientId : '') };
      form.setFieldsValue(values);
      setBody(values.body || '');
      loadModuleMetadata(values.module);
    }).catch((error) => message.error(error?.message || 'Could not load template')).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [form, open, templateId]);

  function insertPlaceholder(field) {
    const token = `{{.${field.key}}}`;
    setBody((current) => `${current}${current ? ' ' : ''}${token}`);
    message.success(`Inserted ${token}`);
  }

  async function save() {
    try {
      const values = await form.validateFields();
      if (!body.trim()) { message.error('Message body is required'); return; }
      setSaving(true);
      const used = extractPlaceholderKeys(values.subject, body);
      const holders = fieldOptions.filter((field) => used.has(field.key)).map((field) => ({ key: field.key, value: field.value }));
      const payload = { ...values, action: Array.isArray(values.action) ? values.action.at(-1) : values.action, body, placeholders: holders.length ? [{ moduleName: values.module, holders }] : [] };
      if (!payload.clientId) delete payload.clientId;
      if (templateId) await updateEmailTemplate(templateId, payload);
      else await createEmailTemplate(payload);
      message.success(templateId ? 'Template updated' : 'Template created');
      onSaved();
    } catch (error) {
      if (!error?.errorFields) message.error(error?.message || 'Could not save template');
    } finally { setSaving(false); }
  }

  return (
    <Drawer open={open} onClose={onClose} width="min(920px, 96vw)" title={templateId ? 'Edit email template' : 'New email template'} destroyOnClose
      extra={<Space><Button onClick={onClose}>Cancel</Button><Button type="primary" loading={saving} onClick={save}>{templateId ? 'Update' : 'Create'}</Button></Space>}>
      <Form form={form} layout="vertical" initialValues={EMPTY} disabled={loading} className="template-editor-form">
        <div className="template-editor-grid">
          <Form.Item name="name" label="Template name" rules={[{ required: true }]}><Input placeholder="Candidate introduction" /></Form.Item>
          <Form.Item name="module" label="Module" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={modules} onChange={(module) => { form.setFieldValue('action', ''); loadModuleMetadata(module); }} /></Form.Item>
          <Form.Item name="action" label="Action" rules={[{ required: true }]} tooltip="Actions come from this module's DB-configured Row Actions."><Select showSearch optionFilterProp="label" options={actionOptions} placeholder="Select configured action" /></Form.Item>
          <Form.Item name="clientId" label="Client scope (optional)"><Select allowClear showSearch optionFilterProp="label" options={clients} placeholder="Available to every client" /></Form.Item>
        </div>
        <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
        <div className="template-editor-grid template-editor-grid--three">
          <Form.Item name="toMail" label="Default To"><Select mode="tags" open={false} tokenSeparators={[',']} /></Form.Item>
          <Form.Item name="ccMail" label="Default Cc"><Select mode="tags" open={false} tokenSeparators={[',']} /></Form.Item>
          <Form.Item name="bccMail" label="Default Bcc"><Select mode="tags" open={false} tokenSeparators={[',']} /></Form.Item>
        </div>
        <Form.Item name="subject" label="Subject" rules={[{ required: true }]}><Input placeholder="Email subject" /></Form.Item>
        <Form.Item label="Message" required><TipTapEditor value={body} onChange={setBody} placeholder="Write the reusable email body…" /></Form.Item>
        <div className="template-placeholder-panel">
          <Text strong>Module placeholders</Text>
          <Text type="secondary">Click to append a DB field token to the message.</Text>
          <div className="template-placeholder-list">
            {fieldOptions.map((field) => <Tag key={field.key} color="blue" onClick={() => insertPlaceholder(field)}>{`{{.${field.key}}}`}</Tag>)}
            {!fieldOptions.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No module fields available" />}
          </div>
        </div>
        <Form.Item name="isActive" label="Available in mail composer" valuePropName="checked"><Switch /></Form.Item>
      </Form>
    </Drawer>
  );
}

function EmailTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [modules, setModules] = useState([]);
  const [clients, setClients] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const moduleOptions = useMemo(() => modules.map((item) => ({ value: item.key ?? item.module ?? item.value, label: item.label ?? item.name ?? item.key ?? item.module ?? item.value })).filter((item) => item.value), [modules]);

  function load(nextPage = page, nextSize = pageSize, nextSearch = search) {
    setLoading(true);
    getEmailTemplates({ offset: (nextPage - 1) * nextSize, limit: nextSize, search: nextSearch })
      .then(({ items: data, totalCount }) => { setItems(data); setTotal(totalCount); })
      .catch((error) => message.error(error?.message || 'Could not load templates'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(1, pageSize, ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([getModules(), getAdminClients({ limit: 200 })]).then(([moduleData, clientData]) => { setModules(moduleData); setClients(clientData); }).catch(() => {});
  }, []);

  async function toggleStatus(record, checked) {
    const id = idOf(record);
    try { await updateEmailTemplate(id, { ...record, isActive: checked }); setItems((current) => current.map((item) => idOf(item) === id ? { ...item, isActive: checked } : item)); }
    catch (error) { message.error(error?.message || 'Could not update status'); }
  }

  async function remove(id) {
    try { await deleteEmailTemplate(id); message.success('Template deleted'); load(1); setPage(1); }
    catch (error) { message.error(error?.message || 'Could not delete template'); }
  }

  const columns = [
    { title: 'Template', key: 'template', width: 300, ellipsis: true, render: (_, record) => <div className="admin-config-primary-cell templates-primary-cell"><Text strong ellipsis={{ tooltip: record.name }}>{record.name}</Text><Text type="secondary" ellipsis={{ tooltip: record.description || record.subject }}>{record.description || record.subject || 'No description'}</Text></div> },
    { title: 'Module', dataIndex: 'module', key: 'module', width: 140, ellipsis: true, responsive: ['sm'], render: (value) => value ? <Tag color="blue">{value}</Tag> : '—' },
    { title: 'Action', dataIndex: 'action', key: 'action', width: 180, ellipsis: true, responsive: ['md'], render: (value) => value ? <Text code ellipsis={{ tooltip: value }}>{value}</Text> : '—' },
    { title: 'Recipients', key: 'recipients', width: 130, responsive: ['lg'], render: (_, record) => `${(record.toMail?.length ?? 0) + (record.ccMail?.length ?? 0) + (record.bccMail?.length ?? 0)} defaults` },
    { title: 'Active', dataIndex: 'isActive', key: 'active', width: 80, render: (value, record) => <Switch size="small" checked={value} onChange={(checked) => toggleStatus(record, checked)} /> },
    { title: '', key: 'actions', width: 84, fixed: 'right', render: (_, record) => <Space size={2}><Tooltip title="Edit"><Button type="text" icon={<EditOutlined />} onClick={() => { setEditingId(idOf(record)); setEditorOpen(true); }} /></Tooltip><Popconfirm title="Delete this template?" onConfirm={() => remove(idOf(record))}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm></Space> },
  ];

  const clientOptions = clients.map((item) => ({ value: item._id ?? item.id, label: item.clientName ?? item.name ?? item._id })).filter((item) => item.value);

  return (
    <div className="fg-section admin-config-section">
      <AppCard icon={<MailOutlined />} title="Email templates" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingId(null); setEditorOpen(true); }}>New template</Button>}>
        <div className="admin-accent-bar" /><Text type="secondary">Create reusable, module-aware email content. Modules, actions and placeholders are read from DB configuration.</Text>
      </AppCard>
      <AppCard title={`${total} template${total === 1 ? '' : 's'}`} extra={<Button icon={<ReloadOutlined />} onClick={() => load()}>Reload</Button>}>
        <div className="admin-config-table-tools"><Input allowClear prefix={<SearchOutlined />} placeholder="Search templates" value={search} onChange={(event) => { const value = event.target.value; setSearch(value); setPage(1); load(1, pageSize, value); }} /></div>
        <div className="templates-table-viewport">
          <Table rowKey={idOf} columns={columns} dataSource={items} loading={loading} className="fg-table" size="small" tableLayout="fixed" sticky scroll={{ x: 914, y: 'calc(100vh - 410px)' }} pagination={{ current: page, pageSize, total, showSizeChanger: true, responsive: true, onChange: (nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); load(nextPage, nextSize); } }} />
        </div>
      </AppCard>
      <TemplateEditor open={editorOpen} templateId={editingId} modules={moduleOptions} clients={clientOptions} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}

function SignatureEditor({ open, signatureId, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (!signatureId) { setName(''); setBody(''); return; }
    getSignature(signatureId).then((item) => { setName(item?.signatureName ?? ''); setBody(item?.signature ?? ''); }).catch(() => message.error('Could not load signature'));
  }, [open, signatureId]);
  async function save() {
    if (!name.trim()) return message.error('Signature name is required');
    if (!body.trim()) return message.error('Signature content is required');
    setSaving(true);
    try {
      const payload = { signatureId, signatureName: name, signature: body, signatureLogoPath: '', placeHolder: [] };
      if (signatureId) await updateSignature(payload); else await createSignature(payload);
      message.success(signatureId ? 'Signature updated' : 'Signature created'); onSaved();
    } catch (error) { message.error(error?.message || 'Could not save signature'); }
    finally { setSaving(false); }
  }
  return <Drawer open={open} onClose={onClose} width="min(760px, 96vw)" title={signatureId ? 'Edit signature' : 'New signature'} destroyOnClose
    extra={<Space><Button onClick={onClose}>Cancel</Button><Button type="primary" loading={saving} onClick={save}>{signatureId ? 'Update' : 'Create'}</Button></Space>}>
    <Form layout="vertical"><Form.Item label="Signature name" required><Input value={name} onChange={(event) => setName(event.target.value)} /></Form.Item><Form.Item label="Signature content" required><TipTapEditor value={body} onChange={setBody} placeholder="Create the reusable signature…" /></Form.Item></Form>
  </Drawer>;
}

function SignatureTemplatesPanel() {
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0); const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [page, setPage] = useState(1); const [pageSize, setPageSize] = useState(20);
  const [editorOpen, setEditorOpen] = useState(false); const [editingId, setEditingId] = useState(null);
  function load(nextPage = page, nextSize = pageSize, nextSearch = search) {
    setLoading(true); getSignatures({ offset: (nextPage - 1) * nextSize, limit: nextSize, search: nextSearch })
      .then(({ items: data, totalCount }) => { setItems(data); setTotal(totalCount); }).catch(() => message.error('Could not load signatures')).finally(() => setLoading(false));
  }
  useEffect(() => { load(1, pageSize, ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function toggle(record, checked) { try { await updateSignatureStatus(record.signatureId, checked); setItems((current) => current.map((item) => item.signatureId === record.signatureId ? { ...item, isActive: checked } : item)); } catch { message.error('Could not update status'); } }
  async function remove(id) { try { await deleteSignature(id); message.success('Signature deleted'); setPage(1); load(1); } catch { message.error('Could not delete signature'); } }
  const columns = [
    { title: 'Signature', dataIndex: 'templateName', key: 'name', width: 300, ellipsis: true, render: (value) => <Text strong ellipsis={{ tooltip: value }}>{value || 'Untitled signature'}</Text> },
    { title: 'Created', dataIndex: 'createdDt', key: 'created', width: 150, responsive: ['md'], render: (value) => value ? new Date(value).toLocaleDateString() : '—' },
    { title: 'Active', dataIndex: 'isActive', key: 'active', width: 80, render: (value, record) => <Switch size="small" checked={value} onChange={(checked) => toggle(record, checked)} /> },
    { title: '', key: 'actions', width: 84, fixed: 'right', render: (_, record) => <Space size={2}><Button type="text" icon={<EditOutlined />} onClick={() => { setEditingId(record.signatureId); setEditorOpen(true); }} /><Popconfirm title="Delete this signature?" onConfirm={() => remove(record.signatureId)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm></Space> },
  ];
  return <div className="fg-section admin-config-section"><AppCard icon={<MailOutlined />} title="Signature templates" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingId(null); setEditorOpen(true); }}>New signature</Button>}><Text type="secondary">Existing company and user signatures from the shared DB.</Text></AppCard><AppCard title={`${total} signature${total === 1 ? '' : 's'}`}><div className="admin-config-table-tools"><Input allowClear prefix={<SearchOutlined />} value={search} placeholder="Search signatures" onChange={(event) => { setSearch(event.target.value); setPage(1); load(1, pageSize, event.target.value); }} /></div><div className="templates-table-viewport"><Table rowKey="signatureId" dataSource={items} columns={columns} loading={loading} className="fg-table" size="small" tableLayout="fixed" sticky scroll={{ x: 614, y: 'calc(100vh - 410px)' }} pagination={{ current: page, pageSize, total, responsive: true, onChange: (p, size) => { setPage(p); setPageSize(size); load(p, size); } }} /></div></AppCard><SignatureEditor open={editorOpen} signatureId={editingId} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} /></div>;
}

export default function TemplatesSection() {
  return <Tabs defaultActiveKey="email" className="admin-config-tabs" items={[
    { key: 'email', label: 'Email templates', children: <EmailTemplatesPanel /> },
    { key: 'signature', label: 'Signatures', children: <SignatureTemplatesPanel /> },
  ]} />;
}
